import React from 'react';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';

import DeveloperWebBasePage from '../DeveloperWebBasePage';

import './tou.scss';

const renderSection1 = () => {
  return (
    <Box>
      <Box my={3}>
        <Typography>
          <strong>1. INTRODUCTION</strong>
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          Thanks for your interest in using Momentive&rsquo;s API for products and services branded as
          &ldquo;SurveyMonkey&rdquo; (&ldquo;Services&rdquo;).{' '}
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          These Terms are a living document and may be changed by Momentive at any time. Momentive will post all changes
          online, and may notify you by email if Momentive regard the changes as significant and material.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection2 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>2. USING THE API</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>2.1. Use Subject to Terms.</strong> Your use of the API and any API Content (including use of the API
          through a third party application that uses the API) is subject to compliance with these Terms. Momentive
          grants you a non-exclusive, non-transferable license (without the right to sublicense) to: (a) use the API
          solely as necessary to develop, test, operate and support your App using certain API Content accessed via the
          API; and (b) distribute or allow access to your integration of the API within your App to end users of your
          App. You may not sell, rent or redistribute access to the API or any API Content to any third party without
          Momentive&rsquo;s prior written approval.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>2.2. General Terms.</strong> If you use the Services as a user, such use remains subject to the
          Momentive <a href="http://www.surveymonkey.com/mp/policy/terms-of-use">Terms of Use</a> for Services purchased
          on Momentive&rsquo;s websites or the Momentive Governing Services Agreement (or similar governing service
          agreement) for Services purchased through Momentive&rsquo;s enterprise sales team{' '}
          <strong>(&ldquo;General Terms&rdquo;)</strong>. Your App&rsquo;s interactions with a Momentive account must
          also comply with and be consistent with the General Terms.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection3 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>3. DEVELOPER ACCOUNTS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>3.1. Developer Account.</strong> Usage of the API is limited to owners of a SurveyMonkey account
          (&ldquo;<strong>Developer Account</strong>&rdquo;). After creation of your Developer Account, you may create
          applications, for which you will receive access credentials (&ldquo;
          <strong>Access Credentials</strong>&rdquo;). Keep your Access Credentials secure and confidential, as you are
          solely responsible for any activities occurring under your Developer Account.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>3.2. Restrictions.</strong> You may open multiple Developer Accounts, but Momentive may close your
          accounts if Momentive regards that you have opened an excessive number of them. Momentive may also close your
          accounts if Momentive believes that you have created multiple accounts to bypass API call limits or
          restrictions. If your Developer Account is suspended, you must not attempt to circumvent such suspension by
          registering for a new Developer Account. You may not open a Developer Account if you are a competitor of
          Momentive.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>3.3. Access Tokens and Credentials.</strong> If Momentive issues an Access Token for your App, you may
          only use that Access Token with that specific App, and cannot use that Access Token with any other
          application. Momentive may also issue credentials for your App. You may not sell, trade, or give your Access
          Token or credentials to any third party without Momentive&rsquo;s prior written consent. You will take
          reasonable measures to safeguard Access Tokens and credentials from unauthorized use or access.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection4 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>4. PRINCIPLES OF USER RESPECT</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          Momentive&rsquo;s goal is to ensure a great experience for Momentive&rsquo;s Users. Momentive treat
          Momentive&rsquo;s Users and their data with respect, so Momentive expects Momentive&rsquo;s API developers to
          do the same.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>4.1 Respect User Data.</strong> Your App must use User data in a way that the User expects. Avoid
          surprising Users by clearly describing what you are doing with their data. Obtain their permission before
          performing actions on their data, such as:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>editing survey data</Typography>
        </li>
        <li>
          <Typography>disclosing survey data to third parties</Typography>
        </li>
        <li>
          <Typography>using survey data in a way not expressly instructed</Typography>
        </li>
        <li>
          <Typography>editing survey questions or survey settings</Typography>
        </li>
        <li>
          <Typography>distributing surveys</Typography>
        </li>
        <li>
          <Typography>closing surveys</Typography>
        </li>
        <li>
          <Typography>altering account settings</Typography>
        </li>
        <li>
          <Typography>taking an account action on a User&rsquo;s behalf</Typography>
        </li>
      </ul>
      <Box my={3}>
        <Typography>
          <strong>4.2.</strong> <strong>Don&rsquo;t Mislead Users.</strong> Your App must not be presented in a way
          which misleads Users. For example, your App must not:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>be publicized in a way which is misleading to Users</Typography>
        </li>
        <li>
          <Typography>use logos or trademarks in a manner that misleads or confuses</Typography>
        </li>
        <li>
          <Typography>impersonate a third party without their authorization</Typography>
        </li>
        <li>
          <Typography>
            link to spam or malware sites or use shortened URLs to mask destinations in a misleading way
          </Typography>
        </li>
      </ul>
      <Box my={3}>
        <Typography>
          <strong>4.3.</strong> <strong>Don&rsquo;t Encourage Bad Behavior. </strong>Your App must not induce your Users
          to act in an inappropriate or illegal manner. Specifically, you must not:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>
            encourage or cause Users to violate any law, terms of use or privacy policy (such as, for example, causing
            or facilitating the User to violate Momentive&rsquo;s{' '}
            <a href="https://www.surveymonkey.com/mp/legal/acceptable-uses-policy/">Acceptable Uses Policy</a> through
            abusive use of email collectors)
          </Typography>
        </li>
        <li>
          <Typography>facilitate or encourage the infringement of intellectual property</Typography>
        </li>
        <li>
          <Typography>
            facilitate or encourage the publishing of a third party&rsquo;s private or confidential information
          </Typography>
        </li>
        <li>
          <Typography>
            encourage collection of information Momentive doesn&rsquo;t allow users to collect (like social security
            numbers, credit card numbers and passwords)
          </Typography>
        </li>
        <li>
          <Typography>
            encourage publishing of links to illegal or malicious content (including age restricted content to minors)
          </Typography>
        </li>
      </ul>
    </Box>
  );
};

const renderSection5 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>5. APP REQUIREMENTS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>5.1.</strong> <strong>Privacy Content.</strong> Your App must include a privacy policy and/or privacy
          notices which describe in reasonably adequate detail how you handle any User data and personally identifiable
          information that may be collected from Users via the API. You must abide by your own privacy notices and
          policies.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>5.2. Disconnection.</strong> You will make it easy for Users to disconnect or disassociate their
          account from your App, and you will not hide or obscure such functionality.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection6 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>6. APP RESTRICTIONS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>6.1. Category Restrictions.</strong> The following types of Apps are not permitted to use the API:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>
            Apps which are not related to facilitating surveys (e.g. you may not use Momentive as an email service
            provider to send out non-survey content).
          </Typography>
        </li>
        <li>
          <Typography>
            Apps which attempt to replicate core functionality of the Services, such as an App which white-labels the
            Services.
          </Typography>
        </li>
        <li>
          <Typography>
            Apps which facilitate the exporting of API Content for importation into a Momentive&rsquo;s
            competitor&rsquo;s product or service.
          </Typography>
        </li>
      </ul>
      <Box my={3}>
        <Typography>
          <strong>6.2. Presentation Restrictions.</strong> Your App cannot replicate, frame or mirror the Momentive
          websites or their design.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>6.3. Functionality Restrictions.</strong> Your App may not:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>Crawl or datamine the API Content without each relevant User or Users&rsquo; consent</Typography>
        </li>
        <li>
          <Typography>
            Use the API to monitor the availability, performance or functionality of any our Services, or for other
            benchmarking or competitive purposes
          </Typography>
        </li>
        <li>
          <Typography>
            Alter, remove, replace or mask any aspect of a Momentive survey without Momentive&rsquo;s prior written
            consent
          </Typography>
        </li>
        <li>
          <Typography>
            Be used in a way which lets Users circumvent any restrictions or limitations placed on their account due to
            the type of Momentive subscription plan they have (for example, the limitation on the number of survey
            responses that can be viewed by a User on a free subscription plan).
          </Typography>
        </li>
      </ul>
    </Box>
  );
};

const renderSection7 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>7. TECHNICAL RESTRICTIONS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>7.1. API Call Limits.</strong> Monentive may impose limits on the number of API calls in any given
          period that your App is permitted to make. Public Apps and Private Apps may be subject to different
          limitations. If Momentive imposes such limits, Momentive may describe them on Momentive&rsquo;s website. If
          you exceed the quantity of API calls you had purchased as specified in your order form, Momentive may charge
          additional fees for the excess. Unused API calls will not roll over to the next period.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>7.2. Excessive Use.</strong> Abusive or excessive use of the API (as determined by Momentive) may
          result in the temporary suspension or termination of your access to the API. If your use of the API is likely
          to generate a volume of API calls materially in excess of what an average API developer would generate, or
          above the limits for your particular App, please contact Momentive first and Momentive may be able to agree to
          an arrangement in relation to your proposed use case.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>7.3 Other Technical Limitations.</strong> Other technical restrictions imposed on Apps are:
        </Typography>
      </Box>
      <ul>
        <li>
          <Typography>
            If you provide an application programming interface that uses the API to return the data of Users, you may
            only return Users&rsquo; usernames or unique identification numbers. You must pull all other User data by
            using the API.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the API to aggregate, cache or store any place or geographic location information contained
            in API Content.
          </Typography>
        </li>
      </ul>
    </Box>
  );
};

const renderSection8 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>8. LEGAL RESTRICTIONS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>8.1. Stay on the Right Side of the Law</strong>. Your App and your App&rsquo;s use of the API must
          comply with all applicable laws, regulations and rules. Your App must not engage in (and must not facilitate)
          spamming, phishing or linking to illegal content. It is your responsibility to be familiar with, and abide by,
          the laws that apply to you.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection9 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>9. MOMENTIVE BRAND ASSETS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>9.1. Use of Momentive Brand Assets.</strong> Momentive&rsquo;s name, logo, and other trademarks
          (&ldquo;Marks&rdquo;) are valuable brand assets of Momentive. Your use of Momentive&rsquo;s name in
          conjunction with your Apps must be in accordance with the rules in this section. Momentive may publish brand
          use or trademark use guidelines from time to time on Momentive&rsquo;s website, including as part of
          Momentive&rsquo;s <a href="https://www.surveymonkey.com/mp/brandassets/">Brand and Trademark Use Policy</a>,
          and use of Momentive&rsquo;s Marks will be subject to all such guidelines. All goodwill arising out of or
          related to the use of the Marks by you shall inure to the benefit of Momentive.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>9.2. No Endorsement.</strong> You may not use Momentive Marks in a way that creates a sense of
          affiliation, endorsement, or sponsorship by Momentive, unless you have Momentive&rsquo;s express written
          permission to do so. Your Apps must be clearly labeled as your own, and not Momentive&rsquo;s. While you may
          use Momentive&rsquo;s name to indicate that your App integrates with or is compatible with the Services, or
          that Momentive was the source of the API Content, you may not label your product as a Momentive product or use
          theMarks as part of your own logo. You may not use Momentive&rsquo;s Marks as part of the name of your company
          or of any App or other product or service created by you.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>9.3. Proprietary Notices.</strong> You may not remove, modify or obfuscate any proprietary notices or
          marks on the API or in any API Content you obtain.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection10 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>10. FEES</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>10.1. Fees.</strong> Access to the API for a basic level of API calls is currently provided at no
          additional fee apart from any applicable fees for your SurveyMonkey account. Contact Sales at Momentive for
          higher API call limit tiers.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection11 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>11. MORE LEGAL TERMS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          <strong>11.1. Limited License to Your Content.</strong> You grant Momentive a worldwide, royalty free license
          to use, reproduce, distribute, modify, adapt, create derivative works, make publicly available, and otherwise
          exploit your copyrights, trademarks, patents, publicity, moral, database, and/or other intellectual property
          rights in and to your App, but only for the limited purposes of promoting and marketing Momentive,
          Momentive&rsquo;s API, and any API content in any media. This license also extends to any trusted third
          parties Momentive works with.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.2. Integration Directory.</strong> Momentive may include and promote your application in
          Momentive&rsquo;s integration directory of applications for Momentive users that have been developed by
          developers. Notwithstanding your application appearing in the integration directory, you agree that Momentive
          may for any reason demote or remove it from the integration directory.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.3. Suspension and Termination.</strong> Momentive will endeavor to warn you if there is an issue
          with your App&rsquo;s compliance with these Terms, or a compliance issue with the manner in which you are
          accessing the API, so that you can attempt to remedy the problem. Notwithstanding the foregoing, if Momentive
          believes that you have violated these terms or are accessing or using the API in any way that Momentive
          regards as abusive, or that causes liability or detriment to Momentive, Momentive may immediately, and without
          notice, suspend or terminate your access to the API, your Developer Account, and any API Content. Momentive
          may terminate these Terms for any reason, with or without notice to the email address associated with your
          Developer Account. You may terminate these Terms for any reason by ceasing to access the API, ceasing to use
          any API Content, and canceling your Developer Account.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.4. Consequences of Termination.</strong> Upon termination of these Terms, you will cease accessing
          the API and will delete all API Content. Sections 3.2 (Restrictions), 9 (Momentive Brand Assets), 11.4
          (Consequences of Termination), and 11.7 (IP Ownership) to 11.14 (Terms for Non-U.S. Developers) will survive
          the termination of these Terms.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.5. Cooperation. </strong> You agree to assist Momentive, at its request, to verify compliance with
          these Terms by providing Momentive with information about your App, including providing Momentive with access
          to it and/or other materials related to your use of the API.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.6. Modifications and Support.</strong> Momentive may provide you with support for the API in
          Momentive&rsquo;s sole discretion. Momentive may modify the API at any time without notice or liability to
          you. If you continue to access the API after such modifications, such act will be deemed your acceptance of
          the modifications.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.7. IP Ownership.</strong> You acknowledge that Momentive and Momentive&rsquo;s Users retain all
          worldwide rights, title and interest (including intellectual property rights) in and to the API Content. You
          acknowledge that Momentive or its licensors retain all rights, title and interest (including intellectual
          property rights) in and to the API and the Marks (and any derivative works or enhancements). You shall not do
          anything inconsistent with such ownership. Rights not expressly granted by these Terms are reserved by
          Momentive.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          Subject to the foregoing, you retain all worldwide rights, title and interest (including intellectual property
          rights) in and to your App. If you provide Momentive with any feedback or comments concerning the API or API
          Content, you agree that Momentive may use, reproduce, license, distribute and exploit in any other way, such
          feedback or comments.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.8. Similar or Competitive Products or Services.</strong> You acknowledge that Momentive may
          independently develop, or have developed for it by other parties, products or services that are similar to or
          otherwise compete with your App, and nothing in these Terms will be construed against that.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.9. Disclaimer of Warranties.</strong> ACCESS TO THE API AND THE API CONTENT IS PROVIDED &ldquo;AS
          IS&rdquo; WITH NO WARRANTY, EXPRESS OR IMPLIED, OF ANY KIND AND MOMENTIVE EXPRESSLY DISCLAIMS ALL WARRANTIES,
          INCLUDING ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AVAILABILITY, SECURITY,
          TITLE, AND NON-INFRINGEMENT. SOME API FEATURES MAY BE EXPERIMENTAL AND UNTESTED. MOMENTIVE DOES NOT REPRESENT
          OR WARRANT THAT THE API IS FREE OF INACCURACIES, ERRORS, BUGS, OR INTERRUPTIONS, OR ARE RELIABLE, ACCURATE,
          COMPLETE, OR OTHERWISE VALID. YOUR USE OF THE API AND API CONTENT IS AT YOUR RISK AND YOU WILL BE SOLELY
          RESPONSIBLE FOR ANY DAMAGE THAT RESULTS FROM USE OF THE API, INCLUDING FOR ANY DAMAGE TO YOUR COMPUTER SYSTEMS
          OR LOSS OF DATA. WHILE MOMENTIVE STRIVES TO MAKE THE API AVAILABLE WITHOUT INTERRUPTION, MOMENTIVE DOES NOT
          GUARANTEE THAT THE API WILL ALWAYS BE AVAILABLE AND DOES NOT PROVIDE ANY GUARANTEES AS TO SERVICE LEVELS.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.10. Indemnity.</strong> You agree to indemnify and hold harmless Momentive, its affiliates, and
          their directors, officers, employees and agents, from and against any third party claim, losses, damages,
          suits, judgments, liability, and litigation costs (including reasonable attorneys&rsquo; fees) arising from or
          related to your use of the API or the API Content or your violation of these Terms.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.11. Limitation of Liability.</strong> YOU AGREE THAT MOMENTIVE SHALL NOT BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO, DAMAGES FOR
          LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES (EVEN IF MOMENTIVE HAS BEEN ADVISED OF THE
          POSSIBILITY OF SUCH DAMAGES), RESULTING FROM YOUR USE OF THE API OR THIRD PARTY PRODUCTS THAT ACCESS API
          CONTENT. IN ANY EVENT, MOMENTIVE&rsquo;S AGGREGATE, CUMULATIVE LIABILITY TO YOU ARISING IN CONNECTION WITH
          THESE TERMS WILL NOT EXCEED US$100.00.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.12. General.</strong> If any provision of these Terms is determined to be invalid or unenforceable
          by a court, such provision shall be deemed severable and the remainder of these Terms shall remain in full
          force and effect. The failure by Momentive to enforce any right or provision of these Terms shall not
          constitute a waiver of that provision or of any other provision of these Terms. You agree that you, and not
          Momentive, is responsible for determining which laws may apply to your use of the API and the API Content and
          assessing your obligations under such laws. These Terms comprise the entire agreement between you and
          Momentive with respect to its subject matter and supersede any prior or contemporaneous understandings,
          representations and other communications (written or oral) between you and Momentive.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.13. Terms for U.S. Developers.</strong> If you are located in the United States, these Terms shall
          be governed by the laws of the State of California, without reference to its conflicts of law rules, and the
          parties hereby submit to the exclusive jurisdiction and venue of the state and federal courts located in that
          State.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          <strong>11.14. Terms for Non-U.S. Developers.</strong> If you are located outside of the United States, these
          Terms shall be governed by the laws of Ireland, and the parties hereby submit to the exclusive jurisdiction of
          the courts of Ireland in relation to any claim arising under or in connection with these Terms which cannot be
          solved through a mutually agreed conciliation procedure.
        </Typography>
      </Box>
    </Box>
  );
};

const renderSection12 = () => {
  return (
    <Box>
      <Typography variant="cardTitle" component="h3">
        <strong>12. DEFINITIONS</strong>
      </Typography>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>Access Token</strong>&rdquo; means a token that permits your App to access or modify data in a
          User&rsquo;s account.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>API</strong>&rdquo; means the application programming interface and associated code, tools,
          documentation and related material that Momentive provides to you in relation to our Services.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>API Content</strong>&rdquo; means any content made available to you through use of the API.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>App</strong>&rdquo; or &ldquo;<strong>Application</strong>&rdquo; means a software application,
          website, product or service that you create or offer.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>Access Credentials</strong>&rdquo; means the security keys, secrets, tokens, passwords,
          usernames and other identifiers and credentials used to access the API.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>Developer Account</strong>&rdquo; means the account for which you are required to register in
          order to obtain the Access Credentials and access to the API.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>Momentive</strong>&rdquo; means Momentive Inc. if you are located in the United States,
          Momentive Brasil Internet Eireli if you are located inside of Brazil, and by Momentive Europe UC if you are
          located elsewhere.
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>Terms</strong>&rdquo; means these Momentive API Developer Terms
        </Typography>
      </Box>
      <Box my={3}>
        <Typography>
          &ldquo;<strong>User</strong>&rdquo; means a Momentive user or a user of your Application that uses the API.
        </Typography>
      </Box>
    </Box>
  );
};

const TOU = () => {
  return (
    <div className="container-tou" data-testid="TOU__Developer">
      <DeveloperWebBasePage id="Developer - Tou" footer={false} title="SurveyMonkey API Developer Portal">
        <div className="tou-page document-view">
          <Box>
            <Box mt={8}>
              <Typography variant="pageTitle" component="h1" align="center">
                <strong>Momentive API Developer Terms</strong>
              </Typography>
              <Typography align="center">
                VERSION DATE: <strong>August 10, 2021</strong>
              </Typography>
              <Typography align="center">LAST UPDATED: August 10, 2021</Typography>
            </Box>

            {renderSection1()}

            {renderSection2()}

            {renderSection3()}

            {renderSection4()}

            {renderSection5()}

            {renderSection6()}

            {renderSection7()}

            {renderSection8()}

            {renderSection9()}

            {renderSection10()}

            {renderSection11()}

            {renderSection12()}

            <Box>
              <Typography variant="cardTitle" component="h3">
                <strong>Changelog</strong>
              </Typography>
              <Box my={3}>
                <Typography>
                  <strong>Jul 22, 2013 updates to Feb 14, 2013 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>Added provisions relating to Access Tokens and secret keys.</Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>Jan 1, 2015 updates to Jul 22, 2013 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>
                    <span>
                      Changed contracting entity from our old Luxembourg entity (SurveyMonkey Europe Sarl) to our Irish
                      entity (SurveyMonkey Europe).
                    </span>
                  </Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>Nov 29, 2016 updates to Jan 1, 2015 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>
                    <span>
                      Changed contracting entity from our old Irish entity (SurveyMonkey Europe) to our new entity
                      (SurveyMonkey Europe UC).
                    </span>
                  </Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>Jan 10, 2017 updates to Nov 29, 2016 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>
                    <span>
                      Clarified that SurveyMonkey may charge for API calls that reach certain limits and these fees and
                      quantities will be specified in the developer&rsquo;s order form. Also clarified that unused API
                      calls do not roll over to the next period.
                    </span>
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <span>
                      Added a license grant to developer&apos;s intellectual property rights for the limited purpose of
                      SurveyMonkey&apos;s promotional activities.
                    </span>
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <span>Clarified when a developer&apos;s App may appear in our integration directory.</span>
                  </Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>Mar 28, 2017 updates to Jan 10, 2017 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>Revise brand and trademark use guidelines.</Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>May 31, 2017 updates to March 28, 2017 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>
                    Clarified SurveyMonkey may independently develop similar or competitive products or services.
                  </Typography>
                </li>
              </ul>
              <Box my={3}>
                <Typography>
                  <strong>August 10, 2021 updates to May 31, 2017 version:</strong>
                </Typography>
              </Box>
              <ul>
                <li>
                  <Typography>
                    Clarified that effective as of July 1, 2021, SurveyMonkey Inc. became Momentive Inc., SurveyMonkey
                    Europe UC became Momentive Europe UC, and SurveyMonkey Brasil Internet Eireli became Momentive
                    Brasil Internet Eireli.
                  </Typography>
                </li>
              </ul>
            </Box>
          </Box>
        </div>
      </DeveloperWebBasePage>
    </div>
  );
};

export default TOU;
